import { twMerge } from 'tailwind-merge';
import { NavLink } from 'react-router-dom';
import { LocalShipping, Person, ManageAccounts, Warehouse, DoorSliding, LocalParking, Forum, Dashboard, Assignment, AssignmentTurnedIn, LocalShippingOutlined, BarChart, AssignmentInd, History } from '@mui/icons-material';
import { Link } from 'react-router-dom';

import logo from '../../assets/freight-roll-logo.png';
import { useUnreadMessageCount } from '../../hooks/useUnreadMessageCount';
import { User, getNavPermissions, isCarrier, isTransport } from '../../utils/roleHelper';

interface SidePanelProps {
  showing: boolean;
  showUserManagement: boolean;
  user: User;
  ymsEnabled: boolean;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  twMerge(
    'px-5 py-3 hover:bg-[#222A33] border-[#2dc3e8] border-l-4 hover:border-[#fb6e52] flex items-center',
    isActive ? 'bg-[#222A33] border-[#fb6e52]' : ''
  );

const SidePanel = ({
  showing,
  showUserManagement,
  user,
  ymsEnabled
}: SidePanelProps) => {
  const { unreadCount } = useUnreadMessageCount();
  const nav = getNavPermissions(user, { ymsEnabled });

  const logoLink = isCarrier(user)
    ? '/carrier/trailers'
    : isTransport(user)
      ? '/transport/trailers'
      : '/shipments';

  const headerLabel = isCarrier(user)
    ? 'My Trailers'
    : isTransport(user)
      ? 'Trailers'
      : 'Dashboard';

  return (
    <div
      className={twMerge(
        'bg-[#323D48] text-white text-sm flex flex-col w-48 fixed h-screen',
        !showing ? 'hidden' : ''
      )}
    >
      <Link to={logoLink}>
        <img src={logo} alt="FreightRoll Logo" className="mr-5" height="70" />
      </Link>
      <h2 className="text-[#979ba7] pb-1 p-5 w-[198px]">{headerLabel}</h2>

      {nav.carrierTrailers && (
        <NavLink to="/carrier/trailers" className={navLinkClass}>
          <LocalShippingOutlined className="mr-1 !h-5" />
          My Trailers
        </NavLink>
      )}

      {nav.transportTrailers && (
        <NavLink to="/transport/trailers" className={navLinkClass}>
          <LocalShippingOutlined className="mr-1 !h-5" />
          Trailers
        </NavLink>
      )}

      {nav.dashboard && (
        <NavLink to="/dashboard" className={navLinkClass}>
          <Dashboard className="mr-1 !h-5" />
          Dashboard
        </NavLink>
      )}

      {nav.shipments && (
        <NavLink to="/shipments" className={navLinkClass}>
          <LocalShipping className="mr-1 !h-5 transform -scale-x-100" />
          Shipments
        </NavLink>
      )}

      {nav.driverMgt && (
        <NavLink to="/drivers/unassigned_slot" className={navLinkClass}>
          <Person className="mr-1 !h-5" />
          Driver Mgt
        </NavLink>
      )}

      {nav.driverComms && (
        <NavLink to="/drivers/comms" className={navLinkClass}>
          <div className="relative mr-1">
            <Forum className="!h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center min-w-[16px] px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>
          Driver Comms
        </NavLink>
      )}

      {nav.dockMgt && (
        <NavLink to="/dock" className={navLinkClass}>
          <DoorSliding className="mr-1 !h-5" />
          Dock Mgt
        </NavLink>
      )}

      {nav.yardSpots && (
        <NavLink to="/yard/spots" className={navLinkClass}>
          <LocalParking className="mr-1 !h-5" />
          Yard Spot Mgt
        </NavLink>
      )}

      {nav.trailerHistory && (
        <NavLink to="/yard/history" className={navLinkClass}>
          <History className="mr-1 !h-5" />
          Trailer History
        </NavLink>
      )}

      {nav.whiteboard && (
        <NavLink to="/whiteboard" className={navLinkClass}>
          <Dashboard className="mr-1 !h-5" />
          White Board
        </NavLink>
      )}

      {nav.taskHistory && (
        <NavLink to="/tasks" className={navLinkClass}>
          <Assignment className="mr-1 !h-5" />
          Task History
        </NavLink>
      )}

      {nav.taskApp && (
        <NavLink to="/taskapp" className={navLinkClass}>
          <AssignmentInd className="mr-1 !h-5" />
          Task App
        </NavLink>
      )}

      {nav.trailers && (
        <NavLink to="/trailers" className={navLinkClass}>
          <LocalShippingOutlined className="mr-1 !h-5" />
          Trailers
        </NavLink>
      )}

      <div className="mt-auto">
        {nav.facilities && (
          <NavLink to="/facilities" className={navLinkClass}>
            <Warehouse className="mr-1 !h-5" />
            Facility Mgt
          </NavLink>
        )}
        {!!showUserManagement && (
          <NavLink to="/users" className={navLinkClass}>
            <ManageAccounts className="mr-1 !h-5" />
            User Mgt
          </NavLink>
        )}
      </div>
    </div>
  );
};

export default SidePanel;
